import { Controller } from "@hotwired/stimulus"

// FD-691: keep the sidebar's own scroll position across navigations.
//
// The sidebar <nav> is its own overflow-y-auto scroll container. Turbo only
// tracks WINDOW scroll (turbo_refreshes_with scroll: :preserve covers same-URL
// morph refreshes; a cross-URL Drive visit swaps the whole <body>), so every
// in-project link click re-rendered the sidebar scrolled back to the top — with
// ~90 projects the user had to hunt for their project again on every click.
//
// We snapshot scrollTop into sessionStorage just before Turbo caches the outgoing
// page, and restore it as soon as the freshly-rendered sidebar connects. The
// server still re-renders the tree each visit, so the active link keeps moving
// with the page (see active_if in _project_nav_tree); only the scroll offset is
// carried over. A same-URL morph refresh mutates this element in place and never
// fires connect(), which is exactly right — its scroll position was never lost.
const STORAGE_KEY = "fd:sidebar-scroll"

export default class extends Controller {
  connect() {
    this.save = this.save.bind(this)
    document.addEventListener("turbo:before-cache", this.save)
    // The sidebar is slotted into <cs-page>, which only lays it out once the page's
    // first render has placed the navigation slot; a scrollTop set before then is lost.
    const page = this.element.closest("cs-page")
    Promise.resolve(page?.updateComplete).then(() => this.restore())
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.save)
  }

  save() {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(this.element.scrollTop))
    } catch (_e) {
      // sessionStorage can throw (private mode / quota) — scroll memory is a
      // nicety, so fail silently rather than interfere with navigation.
    }
  }

  restore() {
    let saved
    try {
      saved = sessionStorage.getItem(STORAGE_KEY)
    } catch (_e) {
      return
    }
    // A stored offset taller than the new page's max simply clamps — better than
    // the previous jump-to-top on every visit.
    if (saved !== null) this.element.scrollTop = parseInt(saved, 10) || 0
  }
}
